import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { deleteUserAccount } from '../lib/user';
import { PasswordInput } from './PasswordInput';
import { PreferencesInfoModal } from './PreferencesInfoModal';
import { OneTrustIcon } from './OneTrustIcon';

export function AccountSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword1, setDeletePassword1] = useState('');
  const [deletePassword2, setDeletePassword2] = useState('');
  const [error, setError] = useState('');
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    magic_link: '',
    token: ''
  });

  useEffect(() => {
    let mounted = true;

    async function getProfile() {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        if (mounted) {
          setUser(user);

          if (user) {
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .single();

            if (profileError) throw profileError;

            if (mounted && profileData) {
              setProfile({
                firstName: profileData.first_name || '',
                lastName: profileData.last_name || '',
                email: user.email || '',
                magic_link: profileData.magic_link || '',
                token: profileData.token || ''
              });
            }
          }
          setLoading(false);
        }
      } catch (error: any) {
        console.error('Error fetching profile:', error);
        if (mounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    }

    getProfile();

    return () => {
      mounted = false;
    };
  }, []);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profile.firstName,
          last_name: profile.lastName,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;
      setError('');
      alert('Profile updated successfully!');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError('Error updating profile. Please try again.');
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (deletePassword1 !== deletePassword2) {
      setError('Passwords do not match');
      return;
    }

    try {
      // First verify the password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: deletePassword1
      });

      if (signInError) {
        setError('Invalid password');
        return;
      }

      // Show final confirmation dialog
      if (window.confirm('Are you absolutely sure you want to delete your account? This action cannot be undone.')) {
        const { success, error: deleteError } = await deleteUserAccount();
        
        if (!success) {
          throw new Error(deleteError || 'Failed to delete account');
        }

        // Navigate to home page after successful deletion
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setError(error.message);
    }
  };

  const handlePreferenceCenter = () => {
    if (profile.magic_link) {
      window.location.href = profile.magic_link;
    } else if (profile.token) {
      window.location.href = `https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/token/2bb33cf0-335b-4ec3-8dd8-764ee4d8f542/${profile.token}`;
    } else {
      window.location.href = 'https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/a32e1c2d-4f67-4544-b6b9-41c4856039d1';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-xl w-1/4 mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-xl w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-xl w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-8">
          Account Settings
        </h1>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-8 transition-all duration-300">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Profile Information</h2>
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={profile.email}
                disabled
                className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 transition-all duration-300"
              />
            </div>

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                className="block w-full rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#499da8] focus:ring-[#499da8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={profile.lastName}
                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                className="block w-full rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#499da8] focus:ring-[#499da8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <p className="text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <button
                type="submit"
                className="px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#499da8] transform hover:-translate-y-0.5 transition-all duration-300"
              >
                Update Profile
              </button>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handlePreferenceCenter}
                  className="px-6 py-3 border border-[#499da8] rounded-xl text-base font-medium text-[#499da8] hover:bg-[#499da8]/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#499da8] transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Manage Preferences
                </button>
                <button
                  type="button"
                  onClick={() => setIsInfoModalOpen(true)}
                  className="relative group"
                  aria-label="Learn more about preference center integration"
                >
                  <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
                  <OneTrustIcon size="lg" />
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 transition-all duration-300">
          <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-6">Delete Account</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Once you delete your account, there is no going back. Please be certain.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-6 py-3 border border-red-600 dark:border-red-500 rounded-xl text-base font-medium text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transform hover:-translate-y-0.5 transition-all duration-300"
            >
              Delete Account
            </button>
          ) : (
            <form onSubmit={handleDeleteAccount} className="space-y-6">
              <PasswordInput
                id="password1"
                value={deletePassword1}
                onChange={(e) => setDeletePassword1(e.target.value)}
                required
                label="Enter your password"
              />

              <PasswordInput
                id="password2"
                value={deletePassword2}
                onChange={(e) => setDeletePassword2(e.target.value)}
                required
                label="Confirm your password"
                error={error}
              />

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletePassword1('');
                    setDeletePassword2('');
                    setError('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Confirm Delete
                </button>
              </div>
            </form>
          )}
        </div>

        <PreferencesInfoModal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
        />
      </div>
    </div>
  );
}